<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTournamentState } from "../../../composables/useTournamentState.ts";

import TournamentLeaderboard from "./TournamentLeaderboard.vue";
import TournamentInfo from "./TournamentInfo.vue";
import TournamentActions from "./TournamentActions.vue";
import TournamentOverview from "./TournamentOverview.vue";
import TournamentMatches from "./TournamentMatches.vue";
import Breadcrumbs from "../util/Breadcrumbs.vue";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const eventId = computed(() => {
  const id = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id;
  return parseInt(id as string);
});

// Use the composable for all tournament state management
const {
  // State
  tournamentState,
  isLoading,
  error,
  successMessage,
  actionLoading,
  matchLoadingStates,
  draggedPlayer,
  isUpdatingPairings,
  showPreviousRounds,

  // Actions
  startTournament,
  randomizeRound,
  advanceToNextRound,
  finishMatch,
  handlePlayerDragStart,
  handlePlayerDrop,
  toggleRoundVisibility,
  replayMatch,
  replayRound,
  replayTournament,

  // Helpers
  isMatchTie,
  getRoundInfo,
  getCompletedMatchesCount,
  isRoundActive,

  // Computed
  matchesByRound,
  isTournamentStarted,
  canStartTournament,
  isTournamentFinished,
  hasUncompletedMatches,
  totalRoundsFromData,
  playerRosters,
} = useTournamentState(eventId.value);

// Handle match updates
const handleUpdateMatchVictoryPoints = (matchId: number, playerIndex: 1 | 2, value: number) => {
  if (tournamentState.value?.matches) {
    const match = tournamentState.value.matches.find((m: any) => m.id === matchId);
    if (match) {
      if (playerIndex === 1) {
        match.player1_victory_points = value;
      } else {
        match.player2_victory_points = value;
      }
    }
  }
};

const handleUpdateMatchModelsCost = (matchId: number, playerIndex: 1 | 2, value: number) => {
  if (tournamentState.value?.matches) {
    const match = tournamentState.value.matches.find((m: any) => m.id === matchId);
    if (match) {
      if (playerIndex === 1) {
        match.player1_models_cost = value;
      } else {
        match.player2_models_cost = value;
      }
    }
  }
};
</script>

<template>
  <Breadcrumbs
  :current-page="'tournament'"
  :event-id="eventId"
/>

  <div class="tournament-management">
    <!-- Error Message -->
    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="success-message">
      {{ successMessage }}
    </div>

    <!-- Tournament Finished Message -->
    <div v-if="isTournamentFinished" class="tournament-finished-message">
      <div class="finished-content">
        <i class="bi bi-trophy-fill"></i>
        <h3>{{ t('tournament.management.finished_title') }}</h3>
        <p>{{ t('tournament.management.finished_subtitle') }}</p>
        <button
          @click="replayTournament"
          class="btn btn-replay-tournament"
          type="button"
          :disabled="actionLoading"
          :title="t('tournament.management.unlock_tournament_title')"
        >
          <i class="bi bi-arrow-counterclockwise"></i>
          {{ actionLoading ? t('tournament.management.unlocking') : t('tournament.management.replay_tournament') }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading">{{ t('tournament.management.loading_state') }}</div>

    <!-- No Tournament State -->
    <div v-else-if="!tournamentState" class="no-tournament">{{ t('tournament.management.load_error') }}</div>

    <!-- Show this only if tournament is NOT started and not enough participants -->
    <div v-else-if="!isTournamentStarted && tournamentState && tournamentState.approved_participants < 2" class="no-participants">
      <p>{{ t('tournament.management.not_enough_participants') }}</p>
      <p>{{ t('tournament.management.approve_participants_hint') }}</p>
    </div>

    <!-- Tournament Content -->
    <div v-else class="tournament-content">
      <!-- Tournament Info -->
      <TournamentInfo
        :approved-participants="tournamentState.approved_participants"
        :current-round="tournamentState.current_round"
        :total-rounds="totalRoundsFromData"
        :is-tournament-started="isTournamentStarted || false"
      />

      <!-- Tournament Overview -->
      <TournamentOverview
        v-if="tournamentState.tournament_rounds && tournamentState.tournament_rounds.length > 0"
        :tournament-rounds="tournamentState.tournament_rounds"
        :current-round="tournamentState.current_round"
        :is-tournament-started="isTournamentStarted || false"
        :is-tournament-finished="isTournamentFinished"
        :can-advance="tournamentState.can_advance || false"
        :action-loading="actionLoading"
        @advance-to-next-round="advanceToNextRound"
      />

      <!-- Tournament Actions -->
      <TournamentActions
      :can-start-tournament="canStartTournament || false"
      :is-tournament-started="isTournamentStarted || false"
      :is-tournament-finished="isTournamentFinished"
      :has-uncompleted-matches="hasUncompletedMatches"
      :can-advance="tournamentState.can_advance || false"
      :action-loading="actionLoading"
      :current-round="tournamentState.current_round || 0"
      :matches-by-round="matchesByRound"
      :is-round-active="isRoundActive"
      :get-completed-matches-count="getCompletedMatchesCount"
      :total-rounds="totalRoundsFromData"
      @start-tournament="startTournament"
      @randomize-round="randomizeRound"
      @advance-to-next-round="advanceToNextRound"
    />

      <!-- Tournament Standings -->
      <TournamentLeaderboard
        v-if="isTournamentStarted"
        :standings="tournamentState.standings"
        :is-finished="isTournamentFinished"
        :show-faction-links="true"
        :player-rosters="playerRosters"
        :current-user-id="tournamentState.current_user_id"
      />

      <!-- Tournament Matches -->
      <TournamentMatches
        v-if="isTournamentStarted"
        :event-id="eventId"
        :is-tournament-started="isTournamentStarted"
        :is-tournament-finished="isTournamentFinished"
        :is-updating-pairings="isUpdatingPairings"
        :action-loading="actionLoading"
        :dragged-player="draggedPlayer || undefined"
        :match-loading-states="matchLoadingStates"
        :show-previous-rounds="showPreviousRounds"
        :matches-by-round="matchesByRound"
        :player-rosters="playerRosters"
        :get-round-info="getRoundInfo"
        :get-completed-matches-count="getCompletedMatchesCount"
        :is-round-active="isRoundActive"
        :is-match-tie="isMatchTie"
        :total-rounds="totalRoundsFromData"
        @player-drag-start="handlePlayerDragStart"
        @player-drop="handlePlayerDrop"
        @finish-match="finishMatch"
        @advance-to-next-round="advanceToNextRound"
        @toggle-round-visibility="toggleRoundVisibility"
        @update-match-victory-points="handleUpdateMatchVictoryPoints"
        @update-match-models-cost="handleUpdateMatchModelsCost"
        @replay-match="replayMatch"
        @replay-round="replayRound"
      />

      <!-- Back Button -->
      <div class="page-actions">
        <button
          @click="router.push({ name: 'show_event', params: { id: eventId } })"
          class="btn btn-back"
        >
          <i class="bi bi-arrow-left"></i>
          {{ t('tournament.management.back_to_event') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tournament-finished-message {
  .finished-content {
    .btn-replay-tournament {
      margin-top: var(--spacing-md);
      background-color: var(--warning-color, #ff9800);
      color: white;
      border: 2px solid var(--warning-color, #ff9800);
      font-weight: bold;
      transition: var(--transition-fast);
      padding: 12px 24px;
      border-radius: var(--border-radius-md);
      font-size: var(--font-size-base);
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);

      &:hover:not(:disabled) {
        background-color: var(--warning-color-dark, #f57c00);
        border-color: var(--warning-color-dark, #f57c00);
        transform: translateY(-1px);
        box-shadow: 0 2px 4px var(--shadow-light);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
        box-shadow: 0 1px 2px var(--shadow-light);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
      }

      i {
        font-size: var(--font-size-lg);
      }
    }
  }
}

.tournament-management {
  padding: var(--spacing-xl);
  background-color: var(--primary-bg);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-color);
}

h3 {
  color: var(--text-primary);
  margin-bottom: var(--spacing-xl);
  font-size: var(--font-size-lg);
  font-weight: bold;
}

.error-message {
  background-color: var(--error-light);
  color: var(--error-text);
  padding: 10px;
  border-radius: var(--border-radius-sm);
  margin-bottom: 15px;
  border: 1px solid var(--error-border);
}

.success-message {
  background-color: var(--success-light);
  color: var(--success-text);
  padding: 10px;
  border-radius: var(--border-radius-sm);
  margin-bottom: 15px;
  border: 1px solid var(--success-border);
}

.tournament-finished-message {
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  padding: 20px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);

  .finished-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    i {
      font-size: 2rem;
      color: #ffd700;
    }

    h3 {
      margin: 0;
      font-size: 1.5rem;
      font-weight: bold;
    }

    p {
      margin: 0;
      opacity: 0.9;
    }
  }
}

.loading,
.no-tournament,
.no-participants {
  text-align: center;
  color: #bd9c65;
  padding: 20px;
}

.tournament-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.page-actions {
  display: flex;
  justify-content: flex-start;
  gap: 15px;
  margin-top: 20px;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;

  &.btn-back {
    background-color: #6c757d;
    color: white;

    &:hover {
      background-color: #5a6268;
      transform: translateY(-1px);
    }
  }

  &:active {
    transform: translateY(0);
  }
}

// Responsive design
@media (max-width: 768px) {
  .tournament-management {
    padding: var(--spacing-md);
  }

  .page-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .tournament-management {
    padding: var(--spacing-sm);
  }
}
</style>
